import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute, Params } from '@angular/router';
import { CognitoUtil, CognitoCallback, LoggedInCallback, ResendCodeCallback } from '../../../services/cognito.service';
import { UserLoginService } from '../../../services/user-login.service';
import { Login } from '../../models/login';
import { ApplicationDataService } from 'services/application.service';
import { AccountService } from '../../../services/account.service';
import { UserRegistrationService } from 'services/user-registration.service';
import { ProspectService } from 'services/prospect.service';
import { BrokerService } from 'services/broker.service';
import { EventEmitterService } from 'services/event-emitter.service';
import { ErrorService } from 'services/error.service';
import { SplashService } from 'services/splash.service';
import { ProspectTourInfo } from 'app/models/prospect-tour-info';
import { GoogleAuthService } from 'services/google-auth.service';
import { IBrokerInfo } from 'app/interface/brokerinfo';
import { environment } from 'environments/environment';

declare function setUserVars(key: any, value: any): void;

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css']
})

export class LoginComponent implements CognitoCallback, LoggedInCallback, OnInit {
  model: Login = new Login();
  renderConfirm: boolean = false;
  processing: boolean = false;
  hidePage: boolean = true;
  refreshParamTags = ["InvitationID", "InviteHRA", "PropertyYardiKey", "CampaignSource", "Campaign", "Adgroup", "Keyword", "Adcopy", "Device", "ResidentHasAccess", "Medium", "TourType", "Source", "YardiID", "YardiKey", "TrackingParameters", "mfunitcode", "DesiredMoveInDate", "DesiredLeaseTerm", "LoginRedirect", "LoginRedirectExternal", "PreserveRedirect"];
  refreshParams = [];
  accessToken: any;
  hide: boolean = true;
  yardiKey: string = null;
  showLoader: boolean = true;
  loadingText: string = "";
  showTourNotavailable: boolean = false;
  render: boolean = false;
  providerType: string;
  redirectUrl: string = null;
  triconPhoneDotFormat = environment.triconPhoneDotFormat;
  triconPhone = environment.triconPhone

  constructor(
    private router: Router,
    public route: ActivatedRoute,
    private userService: UserLoginService,
    private applicationService: ApplicationDataService,
    private accountService: AccountService,
    private cognitoUtil: CognitoUtil,
    private eventEmitterService: EventEmitterService,
    private userRegistration: UserRegistrationService,
    private prospectService: ProspectService,
    private errorService: ErrorService,
    private splashService: SplashService,
    private brokerService: BrokerService,
    private googleAuthService: GoogleAuthService) {
    window.scrollTo(0, 0);
    this.route.params.subscribe(params => this.parseRouteAndSetupLoginRedirect(params));
    this.eventEmitterService.renderConfirmChangeEmitted$.subscribe(res => this.updateRenderConfirm(res));
  }

  async ngOnInit() {
    this.splashService.updateSplash("login");

    this.yardiKey = this.route.snapshot.queryParams['yardiID'] ?? localStorage.getItem('YardiID');

    // Check for InvitationID in query params and store it
    const invitationID = this.route.snapshot.queryParams['InvitationID'];
    if (invitationID) {
      localStorage.setItem('InvitationID', invitationID);
    }

    const currURL = this.router.url;

    if (currURL.includes('id_token')) {
      this.showLoader = true;
      await this.googleAuthService.getAuthInstance();
      if (this.googleAuthService.isAuthenticated) {
        const email = localStorage.getItem('Email');
        this.providerType = localStorage.getItem("ProviderType");
        this.prospectService.checkProspectRecord(email, this.providerType).subscribe(res => this.onCheckProspectRecord(res), res => this.checkProspectError());
      }
    } else if (currURL.includes("email+address+exists")) {
      this.errorService.showError("We’re sorry. The email associated has not been setup for Apple and/or Google sign in. If you think you are receiving this message in error, try using the Forgot Email button or please contact 844-874-2661 for assistance.");
      const errorMsg = window.location.hash;
      console.log("PreSignUp Lambda Error: ", errorMsg);
    }
  }

  cognitoCallback(message: string, result: any) {
    if (message != null) {
      this.processing = false;
      let errorMessage = message;
      if (errorMessage === 'User is not confirmed.') {
        this.errorService.showError("This account has not been confirmed. A new confirmation code is being emailed.")
        this.userRegistration.resendCode(this.model.Email, this);
      }
      else if (errorMessage === 'User needs to set password.') {
        this.renderConfirm = true;
        localStorage.setItem('UpdateID', 'true');
        this.router.navigate(['public/login/newpassword']);
      }
      else if (errorMessage.toLowerCase().indexOf('user does not exist') >= 0) {
        errorMessage = "Incorrect username or password.";
        this.errorService.showError(errorMessage);
      }
      else {
        this.errorService.showError(errorMessage);
      }
    }
    else {
      this.prospectService.checkProspectRecord(this.model.Email, this.providerType).subscribe(res => this.onCheckProspectRecord(res), res => this.checkProspectError());
    }
  }

  resendCodeCallback(error: any, result: any) {
    if (error != null) {
      this.errorService.showError(error);
    } else {
      this.router.navigate(['/public/register/confirm', this.model.Email]);
    }
  }

  checkProspectError() {
    this.errorService.showError("Error processing login. Please try again.");
    this.restoreLocalStorageParams()
  }

  onGetCognitoUserKey(cognitoUserKey: number){
    setUserVars("cognitoUserKey", cognitoUserKey);
  }

  onGetCognitoUserKeyError(){
    console.log("Error getting cognito user key.");
  }

  onCheckProspectRecord(prospectFound: boolean) {
    this.prospectService.getCognitoUserKey().subscribe(res => this.onGetCognitoUserKey(res), res => this.onGetCognitoUserKeyError());

    if (this.redirectUrl) {
      this.performRedirect(this.redirectUrl);
    } else {
      if (localStorage.getItem('TourType') && this.yardiKey) {
        if (prospectFound) {
          this.prospectService.getTourInfo(this.yardiKey)
            .subscribe(
              res => this.onGetTourInfo(res),
              res => {
                console.error("Error processing tour.")
                this.errorService.showError("Error processing tour.");

                this.performFallbackRedirect();
              }
            );
        }
        else {
          localStorage.setItem('LoginRedirect', "/tour/verify");
          this.performRedirect();
        }
      }
      else if(localStorage.getItem('PreserveRedirect') === 'true'){
        const loginRedirectExternal = localStorage.getItem('LoginRedirect');
        localStorage.removeItem('LoginRedirect');
        localStorage.removeItem('PreserveRedirect');
        window.location.href=loginRedirectExternal;
      }
      else {
        if (localStorage.getItem('UpdateID')) {
          const user = this.cognitoUtil.getCurrentUser();
          this.applicationService.updateApplicantID(user.getUsername()).subscribe(res => this.onUpdateApplicantID(), err => this.applicationService.handleError(err));
        }
        else if (localStorage.getItem('InvitationID')) {
          localStorage.setItem('LoginRedirect', "/application/invitation");
          this.performRedirect();
        }
        else {
          this.performFallbackRedirect();
        }
      }
    }
  }

  performFallbackRedirect() {
    this.accountService.getSmarthomeUserStatus().subscribe((res) => {
      if (res.ResidentHasAccess) {
        localStorage.setItem('LoginRedirect', "/smarthome/devices");
      }
      this.performRedirect();
    });
  }

  onGetTourInfo(info: ProspectTourInfo) {
    localStorage.setItem('PropertyAddress', info.Address);
    localStorage.setItem('LoginRedirect', "/tour/agree");
    this.performRedirect();
  }

  parseRouteAndSetupLoginRedirect(params: Params) {
    if (Object.keys(params).length < 1) {
      this.route.queryParams.subscribe(params => {
        if (params['redirect']) {
          this.redirectUrl = params['redirect']
        }
        this.showLoader = false;
        this.userService.isAuthenticated(this);
      })
    } else {
      if (params['username']) {
        this.model.Email = params['username'];
      }
      else if (!this.router.url.includes('id_token') && !params['preserveRedirect'] && localStorage.getItem('PreserveRedirect') !== 'true') {
        localStorage.removeItem('LoginRedirect');
        localStorage.removeItem('LoginRedirectExternal');
        localStorage.removeItem('TourType');
      }

      this.yardiKey = params['yardiID'] ?? localStorage.getItem('YardiID');

      if (params['renew']) {
        localStorage.setItem('YardiID', this.yardiKey);
      }
      if (this.yardiKey) {
        localStorage.setItem('YardiID', this.yardiKey);
      }
      if (localStorage.getItem('InvitationID')) {
        localStorage.setItem('LoginRedirect', "/application/invitation");
      }
      if (localStorage.getItem('PropertyYardiKey')) {
        localStorage.setItem("YardiKey", localStorage.getItem('PropertyYardiKey'));
        localStorage.setItem('LoginRedirect', "application/landing");
      }
      if (params['renew'] === "true") {
        localStorage.setItem('LoginRedirect', "/renewalcog/renewal-dashboard");
      }
      if (params['callbackURL']) {
        localStorage.setItem('LoginRedirectExternal', params['callbackURL']);
      }
      if (params['preserveRedirect']) {
        localStorage.setItem('LoginRedirect', params['preserveRedirect']);
        localStorage.setItem('PreserveRedirect', 'true');
      }
      if (params['manage'] === "true") {
        localStorage.setItem('LoginRedirect', "/account/account-management");
      }
      if (params['dash'] === "true") {
        localStorage.setItem('LoginRedirect', "/account/tours");
      }
      if (params['tourType']) {
        localStorage.setItem('TourType', params['tourType']);

        if (params['tourType'] === "selftour" && this.yardiKey !== null) {
          this.loadingText = "Our system is confirming this home's smart lock status, to ensure a seamless self tour registration process for you.";
          this.prospectService.GetSelfTourDoorLockConfirm(this.yardiKey).subscribe(res => this.onGetSelfTourDoorLockConfirm(res));
        }
        else {
          this.showLoader = false;
          this.userService.isAuthenticated(this);
        }
      } else {
        this.showLoader = false;
        this.userService.isAuthenticated(this);
      }
    }
  }

  performRedirect(url: string = null) {
    if (url) {
      this.router.navigate([url]);
    } else {
      const loginRedirect = localStorage.getItem('LoginRedirect');
      const loginRedirectExternal = localStorage.getItem('LoginRedirectExternal');
      if (loginRedirect) {
        localStorage.removeItem('LoginRedirect');
        this.router.navigate([loginRedirect]);
      }
      else if (loginRedirectExternal) {
        this.cognitoUtil.getAccessToken(this);
      }
      else {
        this.brokerService.getBroker().subscribe(res => this.redirectUserToHome(res));
      }
    }
  }

  redirectUserToHome = (broker:IBrokerInfo) =>{
    if(broker !== null){
      this.router.navigate(['/broker/home']);
    }
    else{
      this.router.navigate(['/application/dashboard']);
    }
  }


  accessTokenCallback(accessToken: any) {
    if (accessToken) {
      this.accessToken = accessToken;
      this.cognitoUtil.getRefreshToken(this);
    }
    else {
      this.errorService.showError("Error logging in.");
    }

  }

  refreshTokenCallback(refreshToken: any) {

    const loginRedirectExternal = localStorage.getItem('LoginRedirectExternal');

    if (refreshToken && loginRedirectExternal) {
      localStorage.removeItem('LoginRedirectExternal');
      window.location.href = `${loginRedirectExternal}#accessToken=${this.accessToken}&refreshToken=${refreshToken.token}`;
    }
    else {
      this.errorService.showError("Error logging in.");
    }
  }

  isLoggedIn(message: string, isLoggedIn: boolean) {
    if (isLoggedIn) {
      this.prospectService.checkProspectRecord(this.model.Email, this.providerType).subscribe(res => this.onCheckProspectRecord(res), res => this.checkProspectError());
    } else {
      this.restoreLocalStorageParams();
    }
  }

  restoreLocalStorageParams() {
    for (let paramtag of this.refreshParamTags) {
      let tempValue = localStorage.getItem(paramtag);

      if (tempValue) {
        let newObj = {};
        newObj[paramtag] = tempValue;
        this.refreshParams.push(newObj);
      }
    }
    localStorage.clear();

    for (let params of this.refreshParams) {
      localStorage.setItem(Object.keys(params)[0], params[Object.keys(params)[0]]);
    }
    this.render = true;
    this.hidePage = false;
  }

  onSubmit() {
    if (this.model.Email == null || this.model.Password == null) {
      this.errorService.showError('All fields are required');
      return;
    }

    if (localStorage.getItem('TourType')) {
      let tourType: string = localStorage.getItem('TourType');
      let yardiID: string = localStorage.getItem('YardiID');
      let trackingParameters: string = localStorage.getItem('TrackingParameters');

      localStorage.clear();
      if (tourType) {
        localStorage.setItem('TourType', tourType);
      }
      if (trackingParameters) {
        localStorage.setItem('TrackingParameters', trackingParameters);
      }
      if (yardiID) {
        localStorage.setItem('YardiID', yardiID);
        this.yardiKey = yardiID;
      }
    }

    this.processing = true;

    this.applicationService.getCaseCorrectEmail(this.model.Email).subscribe(res => this.onEmailCheckResponse(res));
  }

  onEmailCheckResponse(caseCorrectEmail: string) {
    if (caseCorrectEmail) {
      this.userService.authenticate(caseCorrectEmail, this.model.Password, this);
    }
    else {
      this.userService.authenticate(this.model.Email, this.model.Password, this);
    }
  }

  onRegisterClick() {
    this.router.navigate(['/register']);
  }

  onUpdateApplicantID() {
    localStorage.removeItem('UpdateID');
    this.performRedirect();
  }

  updateRenderConfirm(value: boolean) {
    this.renderConfirm = value;
    this.model.Password = null;
  }

  onGetSelfTourDoorLockConfirm(lockConfirm: boolean) {
    if (lockConfirm) {
      this.loadingText = "Our system is confirming this home's availability, to ensure a seamless self tour registration process for you.";
      this.prospectService.getIsPropertyOccupied(this.yardiKey).subscribe(res => this.onGetPropertyOccupied(res), res => this.errorService.showError(res))
    }
    else {
      this.showLoader = false;
      this.loadingText = "";
      this.showTourNotavailable = true;
    }
  }

  onGetPropertyOccupied(isOccupied: boolean){
    this.showLoader = false;
    this.loadingText = "";
    if (isOccupied) {
      this.showTourNotavailable = true;
    }
    else {
      this.userService.isAuthenticated(this);
    }
  }


  onGoogleSignIn() {
    const hostedUIURL = this.googleAuthService.constructGoogleHostedUIUrl();
    window.location.assign(hostedUIURL);
  }
  onAppleSignIn() {
    const hostedUIURL = this.googleAuthService.constructAppleHostedUIUrl();
    window.location.assign(hostedUIURL);
  }
}
